import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rows } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { ALL_WEBHOOK_EVENTS, type WebhookEndpoint, type WebhookEvent } from "@/lib/types";
import { Alert, Badge, Button, Card, EmptyState, Field, PageHeader, Skeleton } from "@/components/ui";

interface CreatedEndpoint {
  endpoint: WebhookEndpoint;
  secret: string;
  notice: string;
}

const eventLabels: Record<WebhookEvent, string> = {
  "shipment.created": "Shipment created",
  "shipment.tracking_updated": "Tracking updated (includes cancellation)",
  "address.created": "Address created",
  "address.archived": "Address archived",
};

/**
 * An account's own outbound integrations: where WeShip posts, and which
 * events it wants. The signing secret follows the same shown-once rule as an
 * API key -- a screen that can display it again is a screen that can leak it.
 */
export function Webhooks() {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [created, setCreated] = useState<CreatedEndpoint | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["webhooks"],
    queryFn: () => api.get<{ data: WebhookEndpoint[] }>("/v1/webhooks"),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedEndpoint>("/v1/webhooks", { url, events }),
    onSuccess: (result) => {
      setCreated(result);
      setUrl("");
      setEvents([]);
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/webhooks/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhooks"] }),
  });

  function toggleEvent(event: WebhookEvent) {
    setEvents((current) => (current.includes(event) ? current.filter((e) => e !== event) : [...current, event]));
  }

  return (
    <>
      <PageHeader
        title="Webhooks"
        subtitle="Tell your own systems what just happened, in real time, instead of polling for it."
      />

      {created && (
        <div className="mb-5">
          <Alert tone="warn">
            <p className="font-medium">{created.notice}</p>
            <code className="mt-2 block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs text-ink-900">
              {created.secret}
            </code>
          </Alert>
        </div>
      )}

      <Card className="mb-5">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">Add an endpoint</h2>
        <Field
          label="URL"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-store.example.com/webhooks/weship"
        />
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-ink-700">Events</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {ALL_WEBHOOK_EVENTS.map((event) => (
              <label key={event} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand"
                  checked={events.includes(event)}
                  onChange={() => toggleEvent(event)}
                />
                {eventLabels[event]}
              </label>
            ))}
          </div>
        </div>
        <Button
          className="mt-4"
          loading={create.isPending}
          disabled={!url.trim() || events.length === 0}
          onClick={() => create.mutate()}
        >
          Add endpoint
        </Button>
        <p className="mt-3 text-xs text-ink-400">
          Every delivery is signed with the secret shown once above. Verify it the same way you would an API webhook
          from any other provider: an HMAC-SHA256 signature over the exact request body.
        </p>
      </Card>

      {isLoading ? (
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : !rows(data).length ? (
        <EmptyState title="No webhook endpoints" body="Add one to hear about a booking or a tracking update as it happens." />
      ) : (
        <div className="ws-card divide-y divide-ink-200">
          {rows(data).map((endpoint) => (
            <div key={endpoint.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-mono text-sm">{endpoint.url}</p>
                  {!endpoint.is_active && <Badge tone="bad">inactive</Badge>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {endpoint.events.map((event) => (
                    <Badge key={event} tone="neutral">
                      {event}
                    </Badge>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-400">Added {dateTime(endpoint.created_at)}</p>
              </div>
              <Button size="sm" variant="danger" onClick={() => remove.mutate(endpoint.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
